import { useState } from "react";
import { Check } from "lucide-react";
import "./ambApply.css";

/* Lands in the shared inbox today. The tag is what lets you filter these
   out from the career centre demo requests, which post to the same form. */
const FORMSPREE_ENDPOINT = "https://formspree.io/f/xdkzvppw";

/* ---------------------------------------------------------------
   Google Form (optional second destination).

   Leave `action` empty and the form posts to Formspree only.

   To turn it on: open the Google Form → ⋮ → "Get pre-filled link",
   type the placeholder shown beside each key below into that field,
   copy the generated URL, and read the entry ids out of it.
   --------------------------------------------------------------- */
const GOOGLE_FORM = {
  action: "", // https://docs.google.com/forms/d/e/<FORM_ID>/formResponse
  fields: {
    firstName: "", // entry.0000000  — prefill with "FIRSTNAME"
    lastName: "", // entry.0000000  — prefill with "LASTNAME"
    email: "", // entry.0000000  — prefill with "EMAIL"
    school: "", // entry.0000000  — prefill with "SCHOOL"
    gradYear: "", // entry.0000000  — prefill with "GRADYEAR"
    social: "", // entry.0000000  — prefill with "SOCIAL"
    plan: "", // entry.0000000  — prefill with "PLAN"
  },
};

const GRAD_YEARS = ["2026", "2027", "2028", "2029", "2030", "Other"];

const EMPTY = {
  firstName: "",
  lastName: "",
  email: "",
  school: "",
  gradYear: "",
  social: "",
  plan: "",
};

const AmbApply = () => {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const change = (e) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await fetch(FORMSPREE_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          _subject: "Ambassador application",
          name: `${form.firstName} ${form.lastName}`.trim(),
          email: form.email,
          school: form.school,
          gradYear: form.gradYear,
          social: form.social,
          plan: form.plan,
          source: "Ambassador page: apply",
        }),
      });
    } catch (err) {
      // Never trap the applicant on a failed network call.
      console.error("Application submission failed:", err);
    }

    /* Second write, so the responses also land in the Sheet. It has to go
       no-cors, which means the reply is opaque and we cannot tell whether
       it succeeded — that is exactly why Formspree above stays wired in as
       the copy you can actually verify. */
    if (GOOGLE_FORM.action) {
      try {
        const body = new FormData();
        Object.entries(GOOGLE_FORM.fields).forEach(([key, entry]) => {
          if (entry) body.append(entry, form[key] ?? "");
        });
        await fetch(GOOGLE_FORM.action, {
          method: "POST",
          mode: "no-cors",
          body,
        });
      } catch (err) {
        console.error("Google Form submission failed:", err);
      }
    }

    setSubmitting(false);
    setSent(true);
  };

  return (
    <section className="amb-apply" id="apply">
      <div className="home-container amb-apply-grid">
        <div className="amb-apply-copy">
          <span className="amb-eyebrow">Apply</span>

          <h2 className="amb-apply-title">Run RAIN on your campus</h2>

          <p className="amb-apply-sub">
            Applications are rolling. Seven questions, about two minutes.
            The last one is the one we actually read.
          </p>
        </div>

        <div className="amb-apply-form-wrap">
          {sent ? (
            <div className="amb-apply-done">
              <span className="amb-apply-tick">
                <Check size={20} strokeWidth={2.6} aria-hidden="true" />
              </span>

              <h3 className="amb-apply-done-title">
                That&rsquo;s in. Thanks, {form.firstName || "and welcome"}.
              </h3>

              <p className="amb-apply-done-body">
                We read every application ourselves. If it looks like a fit
                we&rsquo;ll email you to set up a short call.
              </p>

              <button
                type="button"
                className="amb-apply-again"
                onClick={() => {
                  setForm(EMPTY);
                  setSent(false);
                }}
              >
                Submit another application
              </button>
            </div>
          ) : (
            <form className="amb-apply-form" onSubmit={submit}>
              <div className="amb-apply-row">
                <label className="amb-field">
                  <span>
                    First name <i>*</i>
                  </span>
                  <input
                    type="text"
                    name="firstName"
                    placeholder="John"
                    value={form.firstName}
                    onChange={change}
                    required
                  />
                </label>

                <label className="amb-field">
                  <span>
                    Last name <i>*</i>
                  </span>
                  <input
                    type="text"
                    name="lastName"
                    placeholder="Forman"
                    value={form.lastName}
                    onChange={change}
                    required
                  />
                </label>
              </div>

              <div className="amb-apply-row">
                <label className="amb-field">
                  <span>
                    School email <i>*</i>
                  </span>
                  <input
                    type="email"
                    name="email"
                    placeholder="you@school.edu"
                    value={form.email}
                    onChange={change}
                    required
                  />
                </label>

                <label className="amb-field">
                  <span>
                    School <i>*</i>
                  </span>
                  <input
                    type="text"
                    name="school"
                    placeholder="Duke University"
                    value={form.school}
                    onChange={change}
                    required
                  />
                </label>
              </div>

              <div className="amb-apply-row">
                <label className="amb-field">
                  <span>
                    Graduation year <i>*</i>
                  </span>
                  <select
                    name="gradYear"
                    value={form.gradYear}
                    onChange={change}
                    required
                  >
                    <option value="">Select…</option>
                    {GRAD_YEARS.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="amb-field">
                  <span>LinkedIn or Instagram</span>
                  <input
                    type="text"
                    name="social"
                    placeholder="@yourhandle or a link"
                    value={form.social}
                    onChange={change}
                  />
                </label>
              </div>

              <label className="amb-field">
                <span>
                  What&rsquo;s the first thing you&rsquo;d do on your campus?{" "}
                  <i>*</i>
                </span>
                <textarea
                  name="plan"
                  rows={4}
                  placeholder="An event, a club to partner with, a group chat to post in, whatever you'd start with."
                  value={form.plan}
                  onChange={change}
                  required
                />
              </label>

              <button
                type="submit"
                className="amb-apply-submit"
                disabled={submitting}
              >
                {submitting ? "Sending…" : "Send application"}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

export default AmbApply;
